"use client";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@repo/web-ui/components/base/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/web-ui/components/base/card";
import { Button } from "@repo/web-ui/components/base/button";
import { Input } from "@repo/web-ui/components/base/input";
import { Label } from "@repo/web-ui/components/base/label";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@repo/web-ui/components/base/toggle-group";
import { use, useEffect, useRef, useState } from "react";
import { Handedness, Pitcher } from "@/utils/types";
import { Slider } from "@repo/web-ui/components/base/slider";
import { Canvas } from "@react-three/fiber";
import ArmAngleScene from "@/components/pitch-designer/ArmAngleScene";
import CircleSlider from "@repo/web-ui/components/CircleSlider";
import '@/utils/math/conversions'
import { clockFace } from "@/utils/pitch-designer/circle-slider-labels";
import { getDegToClock } from "@/utils/pitch-designer/getDegToClock";
import { useLocalStore } from "@/utils/localStore";
import { v4 as uuidv4 } from 'uuid'
import { useRouter } from "next/navigation";

const formSchema = z.object({
  name: z.string().min(1),
  heightFt: z.number().min(2, { message: 'Must be between 2-7ft'}).max(7),
  heightIn: z.number(),
});
export default function CreatePitcherPage({searchParams}: {searchParams: Promise<{redirect?: 'create-pitch'}>}) {
  const { redirect } = use(searchParams)
  const router = useRouter()
  const { values, setValues } = useLocalStore()
  const containerRef = useRef<HTMLDivElement>(null!)
  const [handedness, setHandedness] = useState<Handedness>("rh");
  const [elevation, setElevation] = useState<number>(0);
  const [temp, setTemp] = useState<number>(70);
  const [humidity, setHumidity] = useState<number>(.5);
  const [heightFt, setHeightFt] = useState<number>(6)
  const [heightIn, setHeightIn] = useState<number>(0)
  const [defaultArmTiltDeg, setDefaultArmTiltDeg] = useState<number>(45)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      heightFt: 6,
      heightIn: 0,
    },
  });

  // handle changing armTilt on handedness change
  const handleHandednessChange = (value: Handedness) => {
    if (value !== handedness) {
      setDefaultArmTiltDeg(-defaultArmTiltDeg)
    }
    setHandedness(value)
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const newPitcher: Pitcher = {
      id: uuidv4(),
      name: values.name,
      handedness: handedness,
      heightInches: heightFt * 12 +  heightIn,
      defaultAltitudeFt: elevation,
      defaultTemp: temp,
      defaultRelativeHumidity: humidity,
      defaultArmTiltDeg: defaultArmTiltDeg
    }
    setValues ? setValues(prev => ({...prev, PITCHERS: [...prev.PITCHERS, newPitcher]})) : {}
    switch (redirect) {
      case 'create-pitch':
        router.push(`/pitches/create-pitch?pitcherId=${newPitcher.id}`)
        break;
    
      default:
        router.push('/pitches')
        break;
    }
  };
  return (
    <div className="w-full flex justify-center p-4">
          <Card className="w-full max-w-lg">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardHeader>
              <CardTitle>Create pitcher</CardTitle>
              <CardDescription>
                Build a new pitcher that you can assign pitches to.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-8 p-4">
              {/* Name */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter a name for this pitcher" type="text" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Handedness */}
              <FormItem>
                <Label>Handedness</Label>
                <ToggleGroup
                  type="single"
                  value={handedness}
                  onValueChange={(value) =>
                    value ? handleHandednessChange(value as Handedness) : {}
                  }
                >
                  <ToggleGroupItem value="rh">RHP</ToggleGroupItem>
                  <ToggleGroupItem value="lh">LHP</ToggleGroupItem>
                </ToggleGroup>
              </FormItem>

              {/* Height */}
              <div className="flex items-end gap-2">
                <FormField
                  control={form.control}
                  name="heightFt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Height</FormLabel>
                      <FormControl>
                        <Input type="number" min={2} max={7} {...field} className="max-w-20" 
                          value={heightFt} onChange={(e) => setHeightFt(Number(e.currentTarget.value))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <p className="pr-4">ft.</p>
                <FormField
                  control={form.control}
                  name="heightIn"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input type="number" min={0} max={12} {...field} className="max-w-20" 
                          value={heightIn} onChange={(e) => setHeightIn(Number(e.currentTarget.value))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <p>in.</p>
              </div>

              {/* Default Elevation */}
              <FormItem>
                <Label>{`Default Altitude (${Math.round(elevation).toLocaleString()} ft)`}</Label>
                <FormDescription>{'The altitude you pitch at most often. (Sea-level -> 0ft, Denver -> 5,280ft)'}</FormDescription>
                <Slider min={0} max={10000} step={10} value={[elevation]} onValueChange={(value) => setElevation(value[0] ?? 0)} onClick={(e) => e.preventDefault()} />
              </FormItem>

              {/* Default Temp */}
              <FormItem>
                <Label>{`Default Temperature (${Math.round(temp).toLocaleString()}\u00B0F)`}</Label>
                <FormDescription>{'The temperature you pitch at most often.'}</FormDescription>
                <Slider min={-30} max={120} step={1} value={[temp]} onValueChange={(value) => setTemp(value[0] ?? 70)} onClick={(e) => e.preventDefault()} />
              </FormItem>

              {/* Default Relative Humidity */}
              <FormItem>
                <Label>{`Default Relative Humidity (${Math.round(humidity * 100).toLocaleString()}%)`}</Label>
                <FormDescription>{'The relative humidity you pitch at most often.'}</FormDescription>
                <Slider min={0} max={1} step={.01} value={[humidity]} onValueChange={(value) => setHumidity(value[0] ?? 0.5)} onClick={(e) => e.preventDefault()} />
              </FormItem>

              {/* Default Arm angle */}
              <FormItem>
                <Label>{`Default Arm-Angle (${getDegToClock(defaultArmTiltDeg)})`}</Label>
                <FormDescription>The default arm angle to use when creating a pitch for this pitcher.</FormDescription>
                <div className="relative">
                  <Canvas className="border border-border rounded aspect-square">
                    <ArmAngleScene 
                      containerRef={containerRef}
                      handedness={handedness}
                      heightInches={heightFt * 12 + heightIn}
                      armTiltDeg={defaultArmTiltDeg}
                    />
                  </Canvas>
                  <div className="absolute top-0 left-0 bottom-0 right-0 flex justify-center items-center p-4">
                    <CircleSlider 
                      className='data-[slot=handle]:fill-none data-[slot=handle]:stroke-5 data-[slot=track]:stroke-foreground/10'
                      limits={handedness === 'rh' ? {startAngle: 0, sweepAngle: Math.PI} : {startAngle: 0, sweepAngle: -Math.PI}}
                      trackThickness={30}
                      handleSize={40}
                      labels={clockFace}
                      zeroOffest={-Math.PI/2}
                      value={defaultArmTiltDeg.toRadians()} 
                      onValueChange={(value) => setDefaultArmTiltDeg(value.toDegrees())}
                    />
                  </div>
                </div>
              </FormItem>

            </CardContent>
            <CardFooter>
              <Button type="submit" className="w-full">Create</Button>
            </CardFooter>
        </form>
      </Form>
          </Card>
    </div>
  );
}
